import { green } from '@mui/material/colors';
import { CreateVehicleSmartInsightResponse } from '@/api/requests/llm/interfaces';
import { AIPill } from '@/components/Shared/AIPill/AIPill';
import { useAppConstantsContext } from '@/context/AppConstants';
import { useLLM } from '@/hooks/useLLM';
import { Feedback } from '@/reports/shared/Feedback/Feedback';
import { SerializedVehicleReport } from '@/reports/types/serialized';
import { Box, Icons, Stack, Text } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { VehicleSmartInsight } from './VehicleSmartInsight/VehicleSmartInsight';
import { StyledUnlockButton, StyledImage } from './components';

const aiItems = [
  {
    icon: (
      <Icons.GraphicEqOutlined
        sx={{ color: green[500], fontSize: '1.5rem', mr: 0.5 }}
      />
    ),
    text: 'AI Suggestions',
  },
  {
    icon: (
      <Icons.AttachMoneyOutlined
        sx={{ color: green[500], fontSize: '1.5rem', mr: 0.5 }}
      />
    ),
    text: 'Buying & Selling Tips',
  },
  {
    icon: (
      <Icons.TrendingUpOutlined
        sx={{ color: green[500], fontSize: '1.5rem', mr: 0.5 }}
      />
    ),
    text: 'Vehicle Trends',
  },
];

interface SmartInsightsProps {
  reportData: SerializedVehicleReport;
}

const googleFormUrl =
  'https://docs.google.com/forms/d/e/1FAIpQLSeKPPuF5E0OveqkcAbauKnsch-IqPdwWb_dlA7LdxX2Y0GvCg/viewform';

export const SmartInsights = ({ reportData }: SmartInsightsProps) => {
  const {
    images: { imgRadialCar },
  } = useAppConstantsContext();
  const { isMobile } = useScreenSize();
  const {
    data: llmResponse,
    isLoading,
    hasSmartInsights,
    fetchSmartInsights,
  } = useLLM('vehicle', reportData.automobiles[0]) as {
    data: CreateVehicleSmartInsightResponse;
    isLoading: boolean;
    hasSmartInsights: boolean;
    fetchSmartInsights: () => void;
  };

  const report = reportData?.automobiles[0];
  const averageMarketValue =
    report.valuation?.clean_retail?.adjusted || report.prices.average || 0;

  const handleUnlock = () => {
    fetchSmartInsights();
  };

  const contentBoxWidth = () => {
    if (isMobile || hasSmartInsights || isLoading) {
      return '100%';
    }
    return '70%';
  };

  return (
    <Box
      id="smart-insights"
      sx={(theme) => ({
        backgroundImage: `linear-gradient(5deg, ${theme.palette.lineChart.contrastText} 60%, rgba(42,3,69,1) 80%)`,
      })}
    >
      <Box position="relative">
        <StyledImage src={imgRadialCar} alt="radial car" />
      </Box>
      <Box pt={4.5} pb={2.5} px={3.5}>
        <Box width={contentBoxWidth()} position="relative" mb={2.5}>
          <Stack
            display="flex"
            flexDirection="row"
            columnGap={2}
            alignItems="center"
            sx={{ color: 'primary.contrastText' }}
          >
            <Text variant="h1" color="primary.contrastText" m={0}>
              Smart Insights
            </Text>
            <AIPill />
          </Stack>
          <Text
            variant="body1"
            fontWeight="bold"
            mt={0.5}
            mb={{ xs: 2.5, sm: 1 }}
            color="primary.contrastText"
          >
            Access exclusive AI recommended insights into a vehicle!
          </Text>
          {llmResponse?.data || isLoading ? (
            <VehicleSmartInsight averageMarketValue={averageMarketValue} />
          ) : (
            <Box sx={{ color: 'primary.contrastText' }}>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                columnGap={1}
                mb={1.5}
              >
                {aiItems.map((item, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={1.5}>
                    {item.icon}
                    <Text variant="body2" fontWeight="bold">
                      {item.text}
                    </Text>
                  </Box>
                ))}
              </Box>
              <StyledUnlockButton
                role="button"
                variant="contained"
                onClick={handleUnlock}
              >
                Unlock AI Insights
              </StyledUnlockButton>
            </Box>
          )}
        </Box>
        <Text variant="body2" color="rgba(255, 255, 255, 0.7)">
          Disclaimer: Smart Insights includes AI-generated content, which may
          contain errors, inconsistencies, and/or outdated information. Such
          content is provided for informational purposes only and should not be
          relied upon for any specific purpose without verification of its
          accuracy or completeness.
        </Text>
        <Feedback feedbackFormUrl={googleFormUrl} />
      </Box>
    </Box>
  );
};
